import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  ChevronLeft, Lock, Calendar, User, ChevronDown, Plus, Unlock,
  AlertTriangle, Check, Loader2, History, Edit3, X
} from 'lucide-react';
import { toast } from 'sonner';
import { ReadOnlyNoteBody } from '../components/shared/NoteRenderers';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function FinalizedNote() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const [encounter, setEncounter] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  // Addendum state
  const [showAddendum, setShowAddendum] = useState(false);
  const [addendumText, setAddendumText] = useState('');
  const [savingAddendum, setSavingAddendum] = useState(false);
  // Unlock & Amend state
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const encRes = await api.get(`/encounters/${encounterId}`);
        const enc = encRes.data.encounter;
        if (enc && enc.status !== 'finalized') {
          navigate(`/visit/${encounterId}`, { replace: true });
          return;
        }
        setEncounter(enc);
        if (enc?.patient_id) {
          const patRes = await api.get(`/patients/${enc.patient_id}`);
          setPatient(patRes.data.patient);
        }
      } catch {
        toast.error('Failed to load note');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [encounterId, navigate]);

  const handleAddAddendum = async () => {
    if (!addendumText.trim()) { toast.error('Addendum text required'); return; }
    setSavingAddendum(true);
    try {
      const res = await api.post(`/encounters/${encounterId}/addendum`, { text: addendumText.trim() });
      setEncounter(res.data.encounter);
      setAddendumText('');
      setShowAddendum(false);
      toast.success('Addendum added and permanently recorded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add addendum');
    } finally {
      setSavingAddendum(false);
    }
  };

  const handleUnlockAmend = async () => {
    setUnlocking(true);
    try {
      await api.post(`/encounters/${encounterId}/unlock-amend`, {
        reason: unlockReason.trim() || 'Provider amendment',
      });
      toast.success('Note unlocked — previous version snapshotted in audit trail');
      navigate(`/visit/${encounterId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setUnlocking(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get(`/encounters/${encounterId}/amendment-history`);
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-pulse space-y-3 w-full max-w-2xl px-6">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-4 bg-muted rounded w-3/4" />
      </div>
    </div>
  );

  if (!encounter) return <div className="p-6 text-center text-muted-foreground">Note not found</div>;

  const snapshot = encounter.finalized_document;
  const displayPatient = snapshot?.patient || (patient ? { name: patient.name, mrn: patient.mrn } : {});
  const addendums = encounter.note?.addendums || [];
  const isAmended = encounter.amended;
  const amendmentVersion = encounter.amendment_version;

  return (
    <div className="flex flex-col h-full" data-testid="finalized-note" onClick={() => setMenuOpen(false)}>
      {/* Header */}
      <div className="px-6 py-3 border-b bg-white/80 backdrop-blur-xl flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={() => navigate(-1)} data-testid="note-back-btn">
          <ChevronLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {/* Amended badge */}
          {isAmended && (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 gap-1 text-[10px]">
              <Edit3 className="w-2.5 h-2.5" /> Amended (v{amendmentVersion || 2})
            </Badge>
          )}

          {/* Finalized dropdown — replaces static badge */}
          <div className="relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
              data-testid="finalized-menu-btn">
              <Lock className="w-3 h-3" /> Finalized
              <ChevronDown className={`w-3 h-3 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-card rounded-xl border shadow-xl z-50 py-1 animate-in fade-in-0 zoom-in-95 duration-100"
                data-testid="finalized-menu">
                {/* Add Addendum */}
                <button
                  onClick={() => { setMenuOpen(false); setShowAddendum(true); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs hover:bg-accent transition-colors"
                  data-testid="add-addendum-btn">
                  <div className="p-1 rounded-md bg-blue-50 text-blue-600"><Plus className="w-3 h-3" /></div>
                  <div>
                    <p className="font-semibold">Add Addendum</p>
                    <p className="text-[10px] text-muted-foreground">Append info without changing original</p>
                  </div>
                </button>
                {/* Unlock & Amend */}
                <button
                  onClick={() => { setMenuOpen(false); setShowUnlockModal(true); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs hover:bg-accent transition-colors"
                  data-testid="unlock-amend-btn">
                  <div className="p-1 rounded-md bg-amber-50 text-amber-600"><Unlock className="w-3 h-3" /></div>
                  <div>
                    <p className="font-semibold">Unlock & Amend Note</p>
                    <p className="text-[10px] text-muted-foreground">Original snapshotted to audit trail</p>
                  </div>
                </button>
                <div className="border-t my-1" />
                {/* Amendment History */}
                <button
                  onClick={() => { setMenuOpen(false); loadHistory(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs hover:bg-accent transition-colors"
                  data-testid="view-history-btn">
                  <div className="p-1 rounded-md bg-muted text-muted-foreground"><History className="w-3 h-3" /></div>
                  <div>
                    <p className="font-semibold">Amendment History</p>
                    <p className="text-[10px] text-muted-foreground">View previous versions + audit log</p>
                  </div>
                </button>
                {/* Encounter ID tooltip (internal reference) */}
                <div className="border-t mt-1 pt-1 px-3 pb-2">
                  <p className="text-[9px] text-muted-foreground/50">Internal ref: {encounterId.slice(0, 8)}…</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Addendum form (slides in under header) */}
      {showAddendum && (
        <div className="border-b bg-blue-50/30 px-6 py-4 space-y-2.5 animate-in slide-in-from-top-2 duration-150" data-testid="addendum-form">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5 text-blue-600" /> Add Addendum</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">This will be permanently recorded below the note — cannot be edited after saving</p>
            </div>
            <button onClick={() => { setShowAddendum(false); setAddendumText(''); }} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <Textarea
            value={addendumText}
            onChange={e => setAddendumText(e.target.value)}
            placeholder="Enter addendum text..."
            rows={3}
            className="resize-none text-sm"
            autoFocus
            data-testid="addendum-textarea"
          />
          <div className="flex gap-2">
            <Button size="sm" className="text-xs gap-1" onClick={handleAddAddendum} disabled={savingAddendum || !addendumText.trim()} data-testid="addendum-save-btn">
              {savingAddendum ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save Addendum
            </Button>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setShowAddendum(false); setAddendumText(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Unlock & Amend confirmation modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="unlock-modal">
          <div className="bg-card rounded-2xl border shadow-2xl w-full max-w-md p-6 space-y-4 animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-amber-100 shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Space Grotesk' }}>Unlock Note for Amendment</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  The current finalized version will be <strong>permanently saved</strong> as a snapshot in the audit trail. You will be taken back to the note editor to make changes.
                </p>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                  When you re-finalize, the updated note becomes the primary version — the original snapshot remains accessible via <em>Amendment History</em>.
                </p>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Reason for amendment (optional)</label>
              <input
                type="text"
                value={unlockReason}
                onChange={e => setUnlockReason(e.target.value)}
                placeholder="e.g. Coding correction, missed lab order, addendum to A&P..."
                className="w-full mt-1 text-xs border rounded-lg px-3 py-2 bg-background"
                data-testid="unlock-reason-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                className="flex-1 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleUnlockAmend}
                disabled={unlocking}
                data-testid="unlock-confirm-btn">
                {unlocking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                Yes, Unlock & Amend
              </Button>
              <Button variant="outline" className="flex-1 text-xs" onClick={() => setShowUnlockModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Amendment History panel */}
      {showHistory && (
        <div className="border-b bg-muted/20 px-6 py-4 space-y-2" data-testid="amendment-history">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Amendment History</p>
            <button onClick={() => setShowHistory(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No amendments — this is the original note</p>
          ) : (
            <div className="space-y-1.5">
              {history.map((h, i) => (
                <div key={i} className="rounded-lg border bg-card p-3 text-xs">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[9px]">v{h.version}</Badge>
                    <span className="font-medium">Snapshot before amendment</span>
                    <span className="text-muted-foreground ml-auto">{formatDate(h.snapshotted_at)}</span>
                  </div>
                  <p className="text-muted-foreground mt-1">By: {h.snapshotted_by_name} · Reason: {h.reason || 'Not specified'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Document */}
      <ScrollArea className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-8" data-testid="note-document-body">
          {/* Document Header — clean, no encounter UUID */}
          <div className="mb-8 pb-6 border-b">
            <h1 className="text-xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk' }}>
              Clinical Note
              {isAmended && (
                <Badge className="ml-2 text-[10px] bg-amber-50 text-amber-700 border-amber-200 align-middle">
                  Amended
                </Badge>
              )}
            </h1>
            <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-sm">
              {displayPatient.name && (
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Patient:</span>
                  <span className="font-medium">{displayPatient.name.first} {displayPatient.name.last}</span>
                  {displayPatient.mrn && <Badge variant="outline" className="text-[10px] font-mono">{displayPatient.mrn}</Badge>}
                </div>
              )}
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Provider:</span>
                <span className="font-medium">{snapshot?.provider_name || encounter.provider_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Date:</span>
                <span>{formatDate(snapshot?.date || encounter.date)}</span>
              </div>
              {(snapshot?.finalized_at || encounter.finalized_at) && (
                <div className="flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Finalized:</span>
                  <span>{formatDate(snapshot?.finalized_at || encounter.finalized_at)}</span>
                </div>
              )}
              {/* Encounter ID removed from visible header — accessible via the Finalized menu */}
            </div>
          </div>

          {/* Note Body */}
          <ReadOnlyNoteBody snapshot={snapshot} encounter={encounter} />

          {/* Addendums section */}
          {addendums.length > 0 && (
            <div className="mt-10 pt-6 border-t space-y-4" data-testid="addendums-section">
              {addendums.map((addendum, i) => (
                <div key={addendum.id} className="rounded-xl border border-blue-200 bg-blue-50/20 p-4" data-testid={`addendum-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">Addendum</Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {addendum.author_name} · {formatDate(addendum.created_at)}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{addendum.text}</p>
                </div>
              ))}
            </div>
          )}

          {/* Document Footer */}
          <div className="mt-12 pt-6 border-t text-center text-xs text-muted-foreground">
            <p>This document was generated and finalized within the ChartFlow Clinic clinical workflow.</p>
            {(snapshot?.finalized_at || encounter.finalized_at) && (
              <p className="mt-1">Finalized: {formatDate(snapshot?.finalized_at || encounter.finalized_at)}</p>
            )}
            {isAmended && (
              <p className="mt-1 text-amber-600">This note was amended. Previous versions are preserved in the audit trail.</p>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
